/**
 * Table of Duplicate Store attempts. Each row is expandable to reveal the attempt's actions
 * (loaded page by page by AttemptActionsPanel). Shows status, source/target stores, mode, timing and
 * failure reason.
 */
import { useState } from 'react';

import { IStoreCopyAttempt, IStoreCopyStore } from '../../../../../services/api/troubleshooting-service';
import { requiresAttentionCount } from '../action-categories';
import AttemptActionsPanel from './attempt-actions-panel';

interface IAttemptsTableProps {
    attempts: IStoreCopyAttempt[];
}

const STATUS_LABELS: Record<string, string> = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
};

const STALE_MODE_LABELS: Record<string, string> = {
    soft_delete: 'Soft delete',
    disable: 'Disable',
    leave: 'Leave',
};

const formatStore = (store: IStoreCopyStore | null): string => {
    if (!store) return '—';
    return store.name + (store.display_name ? ` (${store.display_name})` : '');
};

const formatDateTime = (value: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const AttemptsTable: React.FC<IAttemptsTableProps> = ({ attempts }) => {
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const toggle = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

    return (
        <div className="attempts-table">
            <div className="attempts-table__row attempts-table__row--head">
                <span />
                <span>ID</span>
                <span>Status</span>
                <span>Source</span>
                <span>Target</span>
                <span>Stale mode</span>
                <span>Started</span>
                <span>Finished</span>
                <span>Actions</span>
            </div>

            {attempts.map((attempt) => {
                const isExpanded = expandedId === attempt.id;
                const attentionCount = requiresAttentionCount(attempt.action_counts);

                return (
                    <div key={attempt.id} className="attempts-table__group">
                        <div
                            className={`attempts-table__row attempts-table__row--body${isExpanded ? ' attempts-table__row--expanded' : ''}`}
                            onClick={() => toggle(attempt.id)}
                        >
                            <span className="attempts-table__chevron">{isExpanded ? '▾' : '▸'}</span>
                            <span className="attempts-table__id">#{attempt.id}</span>
                            <span>
                                <span className={`status-badge status-badge--${attempt.status}`}>
                                    {STATUS_LABELS[attempt.status] ?? attempt.status}
                                </span>
                            </span>
                            <span className="attempts-table__store" title={formatStore(attempt.from_store)}>
                                {formatStore(attempt.from_store)}
                            </span>
                            <span className="attempts-table__store" title={formatStore(attempt.to_store)}>
                                {formatStore(attempt.to_store)}
                            </span>
                            <span>{STALE_MODE_LABELS[attempt.stale_entity_mode] ?? attempt.stale_entity_mode}</span>
                            <span>{formatDateTime(attempt.started_at)}</span>
                            <span>{formatDateTime(attempt.finished_at)}</span>
                            <span className="attempts-table__counts">
                                {attempt.actions_total}
                                {attentionCount > 0 && (
                                    <span className="attempts-table__attention" title="Actions requiring attention">
                                        ⚠ {attentionCount}
                                    </span>
                                )}
                            </span>
                        </div>

                        {isExpanded && (
                            <div className="attempts-table__panel">
                                {attempt.failure_reason && (
                                    <div className="attempts-table__failure">
                                        <strong>Failure reason:</strong> {attempt.failure_reason}
                                    </div>
                                )}
                                <AttemptActionsPanel attempt={attempt} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AttemptsTable;
